import {
  Button,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Switch,
  Table,
  Tag,
} from 'antd';
import {PlusOutlined, InfoCircleOutlined, EditTwoTone} from '@ant-design/icons';

import {ColumnsType, TablePaginationConfig} from 'antd/lib/table';
import {useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../../../../common/hooks';
import {
  chargersList,
  addCharger,
  updateCharger,
  pageNumberChange,
  pageSizeChange,
  searchBy,
  sortBy,
  chargersStatusFilter,
} from './chargersSlice';
import type {ADD_CHARGER, CHARGER_DATA, UPDATE_CHARGER} from './chargers.type';
import checkFormUpdate from '../../../../utils/checkFormUpdate';
import {
  chargerCategoryList,
  clearChargerCategoryFilters,
} from '../chargerCategory/chargerCategorySlice';
import {racksList, clearRacksFilters} from '../../rack/racks/racksSlice';
import {clearStationsFilters, authStationsList} from '../../station/stations/stationsSlice';
import {debounce} from 'lodash';
import {
  debounceDelay,
  sortbyFilter,
  statusFilter,
  statusFilterOptions,
} from '../../../../common/constants';

const Chargers = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.chargers);
  const userPermission = useAppSelector((state) => state.config.config.permissions);
  const [isChargerModalOpen, setIsChargerModalOpen] = useState(false);
  const [isUpdateModal, setIsUpdateModal] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [chargerForm] = Form.useForm();
  const chargerCategoryOptions = useAppSelector(
    (state) => state.chargerCategory.chargerCategories.pageData,
  );
  const stationOptions = useAppSelector((state) => state.stations.stations.pageData);
  const rackOptions = useAppSelector((state) => state.racks.racks.pageData);
  const [modalData, setModalData] = useState<CHARGER_DATA>({} as CHARGER_DATA);

  const handleUpdateCharger = (values: UPDATE_CHARGER) => {
    const payload = checkFormUpdate(values, modalData);

    if (Object.keys(payload).length > 0) {
      dispatch(updateCharger({payload: payload, id: modalData.id}))
        .unwrap()
        .then(() => {
          handleAddChargerModalClose();
          dispatch(
            chargersList({
              page_size: state.chargers.page_size,
              page_number: state.chargers.page_number,
              sort_by: state.chargers.sort_by,
              ascending: state.chargers.ascending,
              search: state.chargers.search,
            }),
          );
        });
    } else {
      message.warning('No field Changed');
    }
  };

  const handleAddCharger = (values: ADD_CHARGER) => {
    dispatch(addCharger(values))
      .unwrap()
      .then(() => {
        handleAddChargerModalClose();
        dispatch(
          chargersList({
            page_size: state.chargers.page_size,
            page_number: state.chargers.page_number,
            sort_by: state.chargers.sort_by,
            ascending: state.chargers.ascending,
            search: state.chargers.search,
          }),
        );
      });
  };

  const handleAddChargerModalClose = () => {
    setIsChargerModalOpen(false);
    chargerForm.resetFields();
    dispatch(clearChargerCategoryFilters());
    dispatch(clearRacksFilters());
    dispatch(clearStationsFilters());
  };

  const handleStationBouncing = debounce((e) => {
    dispatch(authStationsList({search: e, is_active: true}));
  }, debounceDelay);

  const handleChargerCategoryBouncing = debounce((e) => {
    dispatch(chargerCategoryList({search: e, is_active: true}));
  });

  useEffect(() => {
    //
    dispatch(
      chargersList({
        page_size: state.chargers.page_size,
        page_number: state.chargers.page_number,
        sort_by: state.chargers.sort_by,
        ascending: state.chargers.ascending,
        search: state.chargers.search,
        is_active: state.chargers.is_active,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.chargers.page_number,
    state.chargers.page_size,
    state.chargers.search,
    state.chargers.is_active,
    state.chargers.sort_by,
    state.chargers.ascending,
  ]);

  const columns: ColumnsType<CHARGER_DATA> = [
    {
      title: 'S. No.',
      width: 70,
      render: (test, record, index) => {
        return <>{(state.chargers.page_number - 1) * state.chargers.page_size + index + 1}</>;
      },
    },
    {
      title: 'Charger Name',
      dataIndex: 'name',
      sorter: true,
    },
    {
      title: 'Category Name',
      dataIndex: 'category_name',
    },

    {
      title: ' Status',
      dataIndex: 'is_active',
      render: (_, record) => {
        return record.is_active ? (
          <Tag color='success'>Active</Tag>
        ) : (
          <Tag color='error'>Inactive</Tag>
        );
      },
    },

    {
      title: 'Actions',
      key: 'operation',
      render: (_, record) => {
        return (
          <>
            <InfoCircleOutlined
              style={{cursor: 'pointer', fontSize: '16px', color: 'green', marginInline: '10px'}}
              onClick={() => {
                setModalData(record);
                setIsInfoModalOpen(true);
              }}
            />
            {userPermission.update_charger && (
              <EditTwoTone
                style={{cursor: 'pointer', fontSize: '16px', color: 'blue', marginInline: '10px'}}
                onClick={() => {
                  setIsUpdateModal(true);
                  setModalData(record);
                  dispatch(chargerCategoryList({is_active: true}));
                  dispatch(racksList({filters: {is_active: true}}));
                  chargerForm.setFieldsValue({
                    name: record.name,
                    category_id: record.category_id,
                    rack_id: record.rack_id,
                    rack_position: record.rack_position,
                    station_id: record.station_id,
                    is_active: record.is_active,
                  });
                  setIsChargerModalOpen(true);
                }}
              />
            )}
          </>
        );
      },
    },
  ];

  const handleSearch = (e: string) => {
    if (e.length > 3) {
      dispatch(searchBy(e));
    } else if (e.length === 0) {
      dispatch(searchBy(e));
    }
  };

  return (
    <div>
      <div
        style={{
          margin: '10px 0px 10px 0px',
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '350px 1fr 1fr',
        }}
      >
        <Input.Search
          placeholder='Search'
          defaultValue={state.chargers.search}
          style={{borderRadius: '10px'}}
          allowClear
          onSearch={(e) => handleSearch(e)}
        />
        <Select
          style={{width: '100px', marginLeft: '5px'}}
          value={
            state.chargers.is_active === undefined
              ? statusFilter.ALL
              : state.chargers.is_active === true
              ? statusFilter.ACTIVE
              : statusFilter.INACTIVE
          }
          options={statusFilterOptions}
          onChange={(value) => {
            if (value === statusFilter.ACTIVE) {
              dispatch(chargersStatusFilter(true));
            } else if (value === statusFilter.ALL) {
              dispatch(chargersStatusFilter(undefined));
            } else {
              dispatch(chargersStatusFilter(false));
            }
          }}
          showArrow
        />
        <div style={{width: '100%', textAlign: 'end'}}>
          {userPermission.add_charger ? (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              style={{borderRadius: '5px', marginInline: '5px'}}
              onClick={() => {
                setIsUpdateModal(false);
                dispatch(chargerCategoryList({is_active: true}));
                dispatch(authStationsList({is_active: true}));
                setIsChargerModalOpen(true);
              }}
            >
              Add Charger
            </Button>
          ) : null}
        </div>
      </div>
      <Table
        rowKey='id'
        columns={columns}
        pagination={
          {
            total: state.chargers.totalDataCount,
            current: state.chargers.page_number,
            pageSize: state.chargers.page_size,
            showSizeChanger: true,
            // onChange: (pageNumber, pageSize) => {
            //   if (pageSize !== state.chargers.page_size) {
            //     dispatch(pageSizeChange({pageNumber, pageSize}));
            //   } else {
            //     dispatch(pageNumberChange(pageNumber));
            //   }
            // },
          } as TablePaginationConfig
        }
        loading={state.isLoading}
        size='middle'
        dataSource={state.chargers.pageData}
        scroll={{x: undefined, y: '69vh'}}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={(pagination, _, {order, field}: any) => {
          if (pagination.pageSize && pagination.pageSize !== state.chargers.page_size) {
            dispatch(pageSizeChange(pagination.pageSize));
          }
          if (pagination.current && pagination.current !== state.chargers.page_number) {
            dispatch(pageNumberChange(pagination.current));
          }
          if (field) {
            if (order === sortbyFilter.DESCEND) {
              dispatch(sortBy({ascending: false, sortBy: field}));
            } else {
              dispatch(sortBy({ascending: true, sortBy: field}));
            }
          }
        }}
      />
      <Modal
        title={`${modalData?.name}`}
        open={isInfoModalOpen}
        onCancel={() => {
          setIsInfoModalOpen(false);
        }}
        footer={null}
        width={800}
      >
        <Descriptions bordered column={2}>
          <Descriptions.Item label='Charger Name' labelStyle={{fontWeight: 'bolder'}}>
            {modalData?.name}
          </Descriptions.Item>
          <Descriptions.Item label='Charger Id' labelStyle={{fontWeight: 'bolder'}}>
            {modalData?.id}
          </Descriptions.Item>
          <Descriptions.Item label='Is Active' labelStyle={{fontWeight: 'bolder'}} span={2}>
            {modalData?.is_active ? (
              <Tag color='success'>Active</Tag>
            ) : (
              <Tag color='error'> Inactive</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label='Created By' labelStyle={{fontWeight: 'bolder'}}>
            {modalData?.created_by}
          </Descriptions.Item>
          <Descriptions.Item label='Created At' labelStyle={{fontWeight: 'bolder'}}>
            {modalData?.created_at}
          </Descriptions.Item>
          <Descriptions.Item label='Updated By' labelStyle={{fontWeight: 'bolder'}}>
            {modalData?.updated_by}
          </Descriptions.Item>
          <Descriptions.Item label='Updated At' labelStyle={{fontWeight: 'bolder'}}>
            {modalData?.updated_at}
          </Descriptions.Item>
          <Descriptions.Item label='Charger Category ' labelStyle={{fontWeight: 'bolder'}}>
            {modalData?.category_name}
          </Descriptions.Item>
          <Descriptions.Item label='Cabinet Name ' labelStyle={{fontWeight: 'bolder'}}>
            {modalData?.rack_name}
          </Descriptions.Item>
          <Descriptions.Item label='Station Name ' labelStyle={{fontWeight: 'bolder'}}>
            {modalData?.station_name}
          </Descriptions.Item>
        </Descriptions>
      </Modal>
      <Modal
        title={isUpdateModal ? 'Update Charger' : 'Add Charger'}
        open={isChargerModalOpen}
        maskClosable={false}
        onCancel={() => {
          handleAddChargerModalClose();
        }}
        okText={isUpdateModal ? 'Update Charger' : 'Add Charger'}
        onOk={() => {
          chargerForm
            .validateFields()
            .then((values) => {
              //updateChargerForm.resetFields();
              isUpdateModal ? handleUpdateCharger(values) : handleAddCharger(values);
            })
            .catch((info) => {
              console.error('Validate Failed:', info);
            });
        }}
      >
        <Row>
          <Col span={24}>
            <Form name='ADD_CHARGER' form={chargerForm}>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='name'
                    rules={[
                      {required: true, message: 'Please fill Charger Name '},
                      {min: 4, message: 'Minimum length is 4 characters'},
                    ]}
                    label=' Charger Name'
                    labelCol={{span: 24}}
                  >
                    <Input autoComplete='off' placeholder='Charger Name' />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='category_id'
                    rules={[{required: true, message: 'Please fill catagorId'}]}
                    label='Catagory'
                    labelCol={{span: 24}}
                  >
                    <Select
                      placeholder='Please Select'
                      showSearch
                      defaultActiveFirstOption={false}
                      showArrow={false}
                      filterOption={false}
                      onSearch={(e: string) => {
                        if (e.length > 3) {
                          handleChargerCategoryBouncing(e);
                        }
                      }}

                      //loading={roles.isLoading}
                    >
                      {chargerCategoryOptions.map((x, i) => (
                        <Select.Option key={i} value={x.id}>
                          {x.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={11}>
                  <Form.Item
                    name='station_id'
                    rules={[{required: true, message: 'Please fill Station Id'}]}
                    label='Station'
                    labelCol={{span: 24}}
                  >
                    <Select
                      showSearch
                      placeholder='Select Station'
                      showArrow={false}
                      filterOption={false}
                      onSearch={(e: string) => {
                        if (e.length > 3) {
                          handleStationBouncing(e);
                        }
                      }}
                      onChange={(e) => {
                        dispatch(racksList({station_id: [e], filters: {page_size: 100}}));
                        chargerForm.setFieldsValue({rack_id: undefined});
                      }}
                      //loading={roles.isLoading}
                    >
                      {stationOptions.map((x, i) => (
                        <Select.Option key={i} value={x.id}>
                          {x.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='rack_id'
                    rules={[{required: true, message: 'Please fill Cabinet'}]}
                    label='Cabinet'
                    labelCol={{span: 24}}
                  >
                    <Select
                      placeholder='Please Select Cabinet'
                      //showSearch
                      defaultActiveFirstOption={false}
                      showArrow={false}
                      filterOption={false}
                      // onSearch={(e: string) => {
                      //   if (e.length > 3) {
                      //     dispatch(racksList({s{search: e}));
                      //   }
                      // }}
                      //
                      //loading={roles.isLoading}
                    >
                      {rackOptions.map((x, i) => (
                        <Select.Option key={i} value={x.id}>
                          {x.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='rack_position'
                    rules={[{required: true, message: 'Please fill Cabinet Position '}]}
                    label='Cabinet Position'
                    labelCol={{span: 24}}
                  >
                    <InputNumber
                      style={{width: '100%'}}
                      autoComplete='off'
                      placeholder='Cabinet Position'
                    />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  {isUpdateModal ? (
                    <Form.Item name='is_active' label='Is Active' valuePropName='checked'>
                      <Switch checkedChildren='Active' unCheckedChildren='Inactive' />
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name='asset_id'
                      rules={[{required: true, message: 'Please fill asset Id '}]}
                      label=' Asset Id'
                      labelCol={{span: 24}}
                    >
                      <Input autoComplete='off' placeholder='Asset Id' />
                    </Form.Item>
                  )}
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default Chargers;
